import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TraversalCompleteToast } from "../../TraversalCompleteToast";
import { useSeenDiagramOccurrences } from "../useSeenDiagramOccurrences";
import type {
  UseBoardTraversalModeV3Args,
  UseBoardTraversalModeV3Result,
} from "./types";

export const useBoardTraversalModeV3 = ({
  canEdit,
  currentItemTypeId,
  selectedOccurrences,
  isQuantityGroupApproved,
  toggleQuantityGroupApproval,
}: UseBoardTraversalModeV3Args): UseBoardTraversalModeV3Result => {
  const { t } = useTranslation();

  const { seenIds, markSeen } = useSeenDiagramOccurrences({
    itemTypeId: currentItemTypeId,
  });

  const seenCount = useMemo(
    () =>
      selectedOccurrences.filter((occurrence) =>
        seenIds.has(String(occurrence.occurrence_id)),
      ).length,
    [selectedOccurrences, seenIds],
  );

  const showTraversalCompleteToast = useCallback(() => {
    toast(TraversalCompleteToast, {
      autoClose: false,
      position: "bottom-right",
      data: {
        title: t("smartSurveyor.traversal.allSeenTitle"),
        description: t("smartSurveyor.traversal.allSeenDescription"),
        cancelLabel: t("smartSurveyor.common.cancel"),
        approveLabel: t("smartSurveyor.common.yes"),
        onApprove: () => toggleQuantityGroupApproval(currentItemTypeId),
      },
    });
  }, [currentItemTypeId, toggleQuantityGroupApproval, t]);

  const markSeenAndCheckCompletion = useCallback(
    (occurrenceId: string, remainingCount?: number) => {
      if (!currentItemTypeId) return;
      const isNewlySeen = !seenIds.has(occurrenceId);
      markSeen(occurrenceId);
      const totalCount = remainingCount ?? selectedOccurrences.length;
      if (
        canEdit &&
        isNewlySeen &&
        !isQuantityGroupApproved(currentItemTypeId) &&
        seenCount + 1 >= totalCount
      ) {
        showTraversalCompleteToast();
      }
    },
    [
      canEdit,
      currentItemTypeId,
      seenIds,
      seenCount,
      selectedOccurrences,
      markSeen,
      isQuantityGroupApproved,
      showTraversalCompleteToast,
    ],
  );

  return {
    seenIds,
    seenCount,
    markSeen,
    markSeenAndCheckCompletion,
  };
};
